import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import { AnalyticsTracker } from "./components/analytics/AnalyticsTracker";
import { useApp } from "@/hooks/use-app-context";
import { Layout } from "./components/layout/Layout";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import { ProtectedRoute } from "./components/ui/protected-route";
import { ConfirmProvider } from "./components/ui/confirm-dialog";
import { AdminRoute } from "./components/ui/admin-route";
import { AdminLayout } from "./components/admin/AdminLayout";
import { ContributionRouteLayout } from "@/components/shell/ContributionRouteLayout";
import { AdminRedirect } from "./components/admin/AdminRedirect";
import { LEGACY_STATS_REDIRECTS } from "./features/admin-stats/stats-routes";
import { ModeratorRoute } from "./components/ui/moderator-route";
import { ModeratorLayout } from "./components/moderator/ModeratorLayout";
import { ModeratorAnnotateRedirect, ModeratorIndexRedirect, ModeratorTestGradingRoute } from "./pages/moderator/ModeratorRedirects";
import { AccountantRoute } from "./components/ui/accountant-route";
import { CollectorRoute } from "./components/ui/collector-route";
import { OwnSpaceOnly } from "./components/ui/own-space-only";
import { ContributorRoute } from "./components/ui/contributor-route";
import { HomeRoute } from "./features/public-home/HomeRoute";
import { PublicHome } from "./features/public-home/PublicHome";
import { RouteDocumentTitle } from "./hooks/use-document-title";
import LeaderboardPage from "./components/leaderboard/LeaderboardPage";
import Login from "./pages/Login";
import AccountStatusPage from "./pages/AccountStatus";
import Signup from "./pages/Signup";
import DataPolicy from "./pages/data-policy";
import SpeakContribute from "./pages/SpeakContribute";
import LabelImages from "./pages/LabelImages";
import BecomeAdmin from "./pages/BecomeAdmin";
import ValidateAudio from "./pages/ValidateAudio";
import TranscriptionAudio from "./pages/TranscriptionAudio";
import TranscriptionPhrase from "./pages/TranscriptionPhrase";
import ValidateTranscription from "./pages/ValidateTranscription";
import ModelPerformance from "./pages/model-performance";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import MyContributions from "./pages/MyContributions";
import GetPaid from "./pages/GetPaid";
import InitialText from "./pages/InitialText";
import InitialAudio from "./pages/InitialAudio";
import { UserPlaceCachePrivacyBoundary } from "@/features/collection-place";
import { OfflineSyncProvider } from "@/features/offline/OfflineSyncProvider";
import { AppUpdateListener } from "@/pwa/AppUpdateListener";

// Lazy load admin pages for better performance
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import ExcelFiles from "./pages/admin/ExcelFiles";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import ForceChangePassword from "./pages/ForceChangePassword";
import ProfileCompletionGate from "./features/profile-completion/ProfileCompletionGate";
import AuthCallback from "./pages/AuthCallback";
import FirstLoginWelcomeDialog from "@/components/auth/FirstLoginWelcomeDialog";
import ComplaintsSubmit from "./pages/complaints";
const AdminDashboard = lazy(() => import("./pages/admin/dashboard"));
const UsersPage = lazy(() => import("./pages/admin/Users/UsersPage"));
const TranscriptionsList = lazy(() => import("./pages/admin/Contributions/TranscriptionsList"));
const TextContributionsList = lazy(() => import("./pages/admin/Contributions/TextContributionsList"));
const ImagesList = lazy(() => import("./pages/admin/Contributions/images-list"));
const UploadManager = lazy(() => import("./pages/admin/Uploads/upload-manager"));
const ImportedFilesPage = lazy(() => import("./pages/admin/imported-files"));
const AudioContributions = lazy(() => import("./pages/admin/Uploads/AudioContributions"));
const InitialDataContributions = lazy(() => import("./pages/admin/Uploads/InitialDataContributions"));
const ApiConfig = lazy(() => import("./pages/admin/Config/ApiConfig"));
const ModelsConfig = lazy(() => import("./pages/admin/Config/ModelsConfig"));
const LanguageConfig = lazy(() => import("./pages/admin/Config/LanguageConfig"));
const PanelSettings = lazy(() => import("./pages/PanelSettings"));
const ModeratorTodoPage = lazy(() => import("./pages/moderator/ModeratorTodoPage"));
const ModeratorToolsPage = lazy(() => import("./pages/moderator/ModeratorToolsPage"));
const ModeratorPayoutsPage = lazy(() => import("./pages/moderator/ModeratorPayoutsPage"));
const ModeratorPayPage = lazy(() => import("./pages/moderator/ModeratorPayPage"));
const ModeratorValidateAudioPage = lazy(() => import("./pages/moderator/ModeratorValidateAudioPage"));
const ComplaintsAdmin = lazy(() => import("./pages/admin/ComplaintsList"));
const FaqManagement = lazy(() => import("./pages/admin/FaqManagement"));
const AccountantLayout = lazy(() => import("./components/accountant/AccountantLayout"));
const AccountantDashboard = lazy(() => import("./pages/accountant/AccountantDashboard"));
const AccountantRequestsPage = lazy(() => import("./pages/accountant/AccountantRequestsPage"));
const AccountantRequestDetailPage = lazy(() => import("./pages/accountant/AccountantRequestDetailPage"));
const AccountantHistoryPage = lazy(() => import("./pages/accountant/AccountantHistoryPage"));
const AdminContractsPage = lazy(() => import("./pages/admin/AdminContractsPage"));
const ApiKeysPage = lazy(() => import("./pages/admin/ApiKeysPage"));
const AdminPaymentsPage = lazy(() => import("./features/admin-payments/AdminPaymentsPage"));
const MobileAppVersionPage = lazy(() => import("./features/mobile-app-version/MobileAppVersionPage"));
const AdminMetricsPage = lazy(() => import("./features/admin-metrics/AdminMetricsPage"));
const ContributionStatsPage = lazy(() => import("./features/admin-contribution-stats/ContributionStatsPage"));
const CollectionOverviewPage = lazy(() => import("./features/collection-overview/CollectionOverviewPage"));
const AdminStatsPage = lazy(() => import("./features/admin-stats/AdminStatsPage"));
const AdminLogsPage = lazy(() => import("./features/admin-logs/AdminLogsPage"));
const FieldCollectionPage = lazy(() => import("./features/admin-field-collection/FieldCollectionPage"));
const TestModerationPage = lazy(() => import("./pages/admin/TestModerationPage"));
const ModeratorContractsPage = lazy(() => import("./pages/moderator/ModeratorContractsPage"));
const UserContractsPage = lazy(() => import("./pages/user/UserContractsPage"));
const CollectorDashboard = lazy(() => import("./pages/collector/CollectorDashboard"));
const PaymentsHistoryPage = lazy(() => import("./pages/admin/PaymentsHistoryPage"));
const AdminNotFoundPage = lazy(() => import("./pages/admin/NotFoundPage"));
const AdminExcelTranslationsPage = lazy(() => import("./pages/admin/AdminExcelTranslationsPage"));
const RecordingGuidelinesPage = lazy(() => import("./pages/admin/RecordingGuidelinesPage"));
const InitialTranscription = lazy(() => import("./pages/InitialTranscription"));
const CampaignsPage = lazy(() => import("./pages/admin/campaigns/CampaignsPage"));
const InitialReview = lazy(() => import("./pages/InitialReview"));

const queryClient = new QueryClient();

// Loading component for lazy loaded pages
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function ContributionRoute({ children }: { children: ReactNode }) {
  return (
    <ContributionRouteLayout>
      <ProtectedRoute requireAuth requirePolicy requireSectionValidation>
        {children}
      </ProtectedRoute>
    </ContributionRouteLayout>
  );
}

// Redirige tout utilisateur authentifié devant obligatoirement changer son mot de passe
const MustChangePasswordGuard = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user?.mustChangePassword && !location.pathname.includes('/force-change-password')) {
      navigate(`/${location.pathname.split('/')[1] || 'fr'}/force-change-password`, { replace: true });
    }
  }, [user?.mustChangePassword, location.pathname, navigate]);

  return null;
};

// Composant wrapper pour AppProvider à l'intérieur des routes localisées
const LocalizedApp = () => {
  return (
    <AppProvider>
      <ConfirmProvider>
      <OfflineSyncProvider>
      <AppUpdateListener />
      <UserPlaceCachePrivacyBoundary />
      <MustChangePasswordGuard />
      <ProfileCompletionGate />
      <FirstLoginWelcomeDialog />
      <RouteDocumentTitle />
      <Routes>
        {/* Public routes with Layout */}
        {/* The contributor home: admins, moderators and accountants go to their panel. */}
        <Route path="/" element={<OwnSpaceOnly><Layout><HomeRoute /></Layout></OwnSpaceOnly>} />
        <Route path="/dashboard" element={<OwnSpaceOnly><Layout><HomeRoute /></Layout></OwnSpaceOnly>} />
        <Route path="/site" element={<Layout><PublicHome /></Layout>} />
        <Route path="/leaderboard" element={<Layout><LeaderboardPage /></Layout>} />
        {/* Auth screens bring their own frame (AuthShell): no site header or footer. */}
        <Route path="/login" element={<Login />} />
        <Route path="/account-status" element={<AccountStatusPage />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/data-policy" element={<Layout><DataPolicy /></Layout>} />
        <Route path="/complaints" element={<Layout><ComplaintsSubmit /></Layout>} />
        <Route path="/become-admin" element={<Layout><BecomeAdmin /></Layout>} />
        
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/force-change-password" element={<ForceChangePassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />


        {/* Protected contribution routes stay inside each back-office shell. */}
        <Route
          path="/speak-contribute"
          element={<ContributionRoute><SpeakContribute /></ContributionRoute>}
        />
        <Route
          path="/label-images"
          element={<ContributionRoute><LabelImages /></ContributionRoute>}
        />
        <Route
          path="/validate-audio"
          element={<ContributionRoute><ValidateAudio /></ContributionRoute>}
        />
        <Route
          path="/validate-transcription"
          element={<ContributionRoute><ValidateTranscription /></ContributionRoute>}
        />
        <Route
          path="/transcription"
          element={<ContributionRoute><TranscriptionAudio /></ContributionRoute>}
        />
        <Route
          path="/transcription-phrase"
          element={<ContributionRoute><TranscriptionPhrase /></ContributionRoute>}
        />
        {/* Model benchmarking is an admin tool now; old links land in the admin space. */}
        <Route path="/model-performance" element={<Navigate to="../admin/config/model-performance" replace />} />
        <Route
          path="/initial-text"
          element={<ContributionRoute><InitialText /></ContributionRoute>}
        />
        <Route
          path="/initial-audio"
          element={<ContributionRoute><InitialAudio /></ContributionRoute>}
        />
        <Route
          path="/initial-transcription"
          element={<ContributionRoute><Suspense fallback={<LoadingFallback />}><InitialTranscription /></Suspense></ContributionRoute>}
        />
        <Route
          path="/initial-review"
          element={<ContributionRoute><Suspense fallback={<LoadingFallback />}><InitialReview /></Suspense></ContributionRoute>}
        />
        <Route
          path="/my-contributions"
          element={
            <Layout>
              <ProtectedRoute requireAuth={true} requirePolicy={true} requireSectionValidation={true}>
                <MyContributions />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/get-paid"
          element={
            <ProtectedRoute requireAuth={true} requirePolicy={true} requireSectionValidation={true}>
              <ContributorRoute><GetPaid /></ContributorRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <Layout>
              <ProtectedRoute requireAuth={true} requirePolicy={true} requireSectionValidation={true}>
                <Settings />
              </ProtectedRoute>
            </Layout>
          }
        />
        
        <Route
          path="/user/contracts"
          element={
            <ProtectedRoute requireAuth={true} requirePolicy={true} requireSectionValidation={true}>
              <ContributorRoute>
                <Suspense fallback={<LoadingFallback />}><UserContractsPage /></Suspense>
              </ContributorRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/collector"
          element={
            <CollectorRoute>
              <Suspense fallback={<LoadingFallback />}>
                <CollectorDashboard />
              </Suspense>
            </CollectorRoute>
          }
        />

        {/* Moderator Route */}
        <Route path="/moderator/*" element={<ModeratorRoute><ModeratorLayout /></ModeratorRoute>}>
          {/* Four entries: to do (grading and payout reviews open from it), audio validation, contracts, tools. */}
          <Route index element={<ModeratorIndexRedirect />} />
          <Route path="todo" element={<Suspense fallback={<LoadingFallback />}><ModeratorTodoPage /></Suspense>} />
          <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><PanelSettings /></Suspense>} />
          <Route path="settings" element={<Navigate to="../profile" replace />} />
          <Route path="get-paid" element={<ModeratorTestGradingRoute />} />
          <Route path="payouts" element={<Suspense fallback={<LoadingFallback />}><ModeratorPayoutsPage /></Suspense>} />
          <Route path="validate-audio" element={<Suspense fallback={<LoadingFallback />}><ModeratorValidateAudioPage /></Suspense>} />
          <Route path="contracts" element={<Suspense fallback={<LoadingFallback />}><ModeratorContractsPage /></Suspense>} />
          <Route path="tools" element={<Suspense fallback={<LoadingFallback />}><ModeratorToolsPage /></Suspense>} />
          <Route path="excel-translations" element={<ModeratorAnnotateRedirect />} />
          <Route path="pay" element={<Suspense fallback={<LoadingFallback />}><ModeratorPayPage /></Suspense>} />
        </Route>

        <Route
          path="accountant/*"
          element={
            <AccountantRoute>
              <Suspense fallback={<LoadingFallback />}>
                <AccountantLayout />
              </Suspense>
            </AccountantRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AccountantDashboard />} />
          <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><PanelSettings /></Suspense>} />
          <Route path="settings" element={<Navigate to="../profile" replace />} />
          <Route path="requests" element={<AccountantRequestsPage />} />
          <Route path="requests/:id" element={<AccountantRequestDetailPage />} />
          <Route path="history" element={<AccountantHistoryPage />} />
        </Route>

        {/* Admin routes with AdminLayout */}
        <Route
          path="admin/*"
          element={
            <AdminRoute requiredRole="admin">
              <Suspense fallback={<LoadingFallback />}>
                <AdminLayout />
              </Suspense>
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><PanelSettings /></Suspense>} />
          <Route path="settings" element={<Navigate to="../profile" replace />} />
          <Route path="complaints" element={<Suspense fallback={<LoadingFallback />}><ComplaintsAdmin /></Suspense>} />
          <Route path="faq" element={<Suspense fallback={<LoadingFallback />}><FaqManagement /></Suspense>} />
          <Route path="recording-guidelines" element={<Suspense fallback={<LoadingFallback />}><RecordingGuidelinesPage /></Suspense>} />
          <Route path="dashboard" element={<AdminDashboard />} />
          {/* One statistics page, three tabs; the former pages redirect to their tab. */}
          <Route path="stats" element={<Suspense fallback={<LoadingFallback />}><AdminStatsPage /></Suspense>}>
            <Route index element={<AdminRedirect to="stats/collection" />} />
            <Route path="collection" element={<CollectionOverviewPage />} />
            <Route path="types" element={<ContributionStatsPage />} />
            <Route path="indicators" element={<AdminMetricsPage />} />
          </Route>
          {Object.entries(LEGACY_STATS_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<AdminRedirect to={to} />} />
          ))}
          <Route path="logs" element={<Suspense fallback={<LoadingFallback />}><AdminLogsPage /></Suspense>} />
          <Route path="test-moderation" element={<Suspense fallback={<LoadingFallback />}><TestModerationPage /></Suspense>} />
          <Route path="users" element={<UsersPage />} />
          <Route path="campaigns" element={<Suspense fallback={<LoadingFallback />}><CampaignsPage /></Suspense>} />
          <Route path="api-keys" element={<AdminRoute requiredRole="super_admin"><Suspense fallback={<LoadingFallback />}><ApiKeysPage /></Suspense></AdminRoute>} />
          <Route path="field-collection" element={<Suspense fallback={<LoadingFallback />}><FieldCollectionPage /></Suspense>} />
          <Route path="contributions/audio" element={<AudioContributions />} />
          <Route path="contributions/text" element={<TextContributionsList />} />
          <Route path="contributions/transcriptions" element={<TranscriptionsList />} />
          <Route path="uploads">
            <Route index element={<Navigate to="audio" replace />} />
            <Route path="files" element={<Suspense fallback={<LoadingFallback />}><ImportedFilesPage /></Suspense>} />
            <Route path="phrases" element={<AdminRedirect to="uploads-excels?tab=phrases" />} />
            <Route path=":type" element={<UploadManager />} />
            <Route path="audio-contributions" element={<Navigate to="../../contributions/audio" replace />} />
            <Route path="text-contributions" element={<Navigate to="../../contributions/text" replace />} />
            <Route path="audio-transcript-contributions" element={<Navigate to="../../contributions/transcriptions" replace />} />
            <Route path="audio-transscript-contributions" element={<Navigate to="../../contributions/transcriptions" replace />} />
            <Route path="initial-data-contributions" element={<Navigate to="../../contributions/initial-data" replace />} />
            <Route path="initial-data-contribution" element={<Navigate to="../../contributions/initial-data" replace />} />
          </Route>
          {/* audio, text and transcriptions are declared flat above: on equal paths
              React Router keeps the first declaration, so nested copies were never reached. */}
          <Route path="contributions">
            <Route index element={<Navigate to="audio" replace />} />
            <Route path="images" element={<ImagesList />} />
            <Route path="initial-data" element={<Suspense fallback={<LoadingFallback />}><InitialDataContributions /></Suspense>} />
          </Route>
          <Route path="config/api" element={<ApiConfig />} />
          <Route path="config/models" element={<ModelsConfig />} />
          <Route path="config/languages" element={<LanguageConfig />} />
          {/* Themes are created on "Themes and phrases" now. */}
          <Route path="config/theme" element={<AdminRedirect to="uploads-excels?tab=new" />} />
          <Route path="config/model-performance" element={<ModelPerformance />} />
          <Route path="config/mobile-app" element={<AdminRoute requiredRole="super_admin"><Suspense fallback={<LoadingFallback />}><MobileAppVersionPage /></Suspense></AdminRoute>} />
          <Route path="uploads-excels" element={<ExcelFiles />} />
          <Route path="excel-translations" element={<Suspense fallback={<LoadingFallback />}><AdminExcelTranslationsPage /></Suspense>} />
          <Route path="contracts" element={<Suspense fallback={<LoadingFallback />}><AdminContractsPage /></Suspense>} />
          <Route path="payments" element={<Suspense fallback={<LoadingFallback />}><AdminPaymentsPage /></Suspense>} />
          <Route path="payments/history" element={<Suspense fallback={<LoadingFallback />}><PaymentsHistoryPage /></Suspense>} />
          <Route path="*" element={<Suspense fallback={<LoadingFallback />}><AdminNotFoundPage /></Suspense>} />
        </Route>

        <Route path="*" element={<Layout><NotFound /></Layout>} />
      </Routes>
      </OfflineSyncProvider>
      </ConfirmProvider>
    </AppProvider>
  );
};

// Composant pour les routes localisées
const LocalizedRoutes = () => {
  const { lang } = useParams<{ lang: string }>();

  // Vérifier si la langue est valide
  if (!lang || !['fr', 'en'].includes(lang)) {
    return <Navigate to="/fr" replace />;
  }

  return <LocalizedApp />;
};

// Composant pour détecter et rediriger vers la bonne langue
const LanguageDetector = () => {
  // Détecter la langue du navigateur
  const browserLang = navigator.language.startsWith('fr') ? 'fr' : 'en';

  // Rediriger vers la langue détectée
  return <Navigate to={`/${browserLang}`} replace />;
};

const App = () => (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <Routes>
            {/* Redirection racine vers détection de langue */}
            <Route path="/" element={<LanguageDetector />} />

            {/* Routes localisées avec AppProvider à l'intérieur */}
            <Route path="/:lang/*" element={<LocalizedRoutes />} />

            {/* Catch-all pour les routes invalides */}
            <Route path="*" element={<Navigate to="/fr" replace />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
);

export default App;
